import { locale } from '../lib/locale';

/**
 * Settings → Kalender & Müllabfuhr (1.19): connected calendars, the calendar assistant, the
 * family calendar on the phone, the waste collection wizard. Provider steps use the names
 * Google, Microsoft and Apple show in their English apps. German is the master.
 */
export const CAL_SETTINGS_TEXT = {
  de: {
    wasteLink: 'Müllkalender (Link)',
    calendarLink: 'Kalender-Link',
    updated: (time: string) => ` · aktualisiert ${time}`,
    loading: ' · wird geladen …',
    remindFor: (title: string) => `Erinnern bei ${title}`,
    refresh: (title: string) => `${title} jetzt aktualisieren`,
    remove: (title: string) => `${title} entfernen`,
    nothing: 'Noch nichts verbunden.',
    connect: 'Kalender verbinden',
    waste: 'Müllabfuhr',
    remindTitle: (title: string) => `Erinnern – ${title}`,
    remindHint: 'Gilt für alle Termine aus diesem Kalender – ab dem nächsten Abgleich.',
    done: 'Fertig',
    providers: {
      google: {
        label: 'Google',
        name: 'Google-Kalender',
        open: 'Google-Kalender-Einstellungen öffnen',
        steps: [
          'Links unter „Einstellungen für meine Kalender“ den Kalender antippen.',
          'Ganz nach unten zu „Geheime Adresse im iCal-Format“ – Kopieren.',
          'Hier unten einfügen und „Prüfen“ tippen.',
        ],
      },
      outlook: {
        label: 'Outlook',
        name: 'Outlook-Kalender',
        open: 'Outlook „Freigegebene Kalender“ öffnen',
        steps: [
          'Bei „Kalender veröffentlichen“ den Kalender und „Kann alle Details anzeigen“ wählen.',
          '„Veröffentlichen“ – dann den ICS-Link kopieren.',
          'Hier unten einfügen und „Prüfen“ tippen.',
        ],
      },
      apple: {
        label: 'Apple / iCloud',
        name: 'iCloud-Kalender',
        open: 'iCloud-Kalender öffnen',
        steps: [
          'Neben dem Kalender auf das Teilen-Symbol tippen.',
          '„Öffentlicher Kalender“ einschalten und den Link kopieren.',
          'Hier unten einfügen und „Prüfen“ tippen.',
        ],
      },
      other: {
        label: 'Schule, Verein …',
        name: '',
        open: '',
        steps: [
          'Auf der Webseite nach „Kalender abonnieren“, „iCal“ oder „ICS“ suchen.',
          'Den Link kopieren (lange drücken → Link kopieren).',
          'Hier unten einfügen und „Prüfen“ tippen.',
        ],
      },
      waste: {
        label: 'Müllabfuhr',
        name: 'Müllabfuhr',
        open: '',
        steps: [
          'Den Abfuhrkalender eurer Stadt oder eures Abfallbetriebs öffnen und eure Straße wählen.',
          'Nach „Kalender abonnieren“, „iCal“ oder „ICS“ suchen und den Link kopieren (lange drücken → Link kopieren).',
          'Hier unten einfügen und „Prüfen“ tippen.',
        ],
      },
    },
    copied: 'Kopiert – z. B. per Mail an dich schicken',
    where: 'Wo ist der Kalender?',
    computerOnly: 'Das geht nur am Computer im Browser – die Kalender-App zeigt diesen Link nicht.',
    copyForComputer: 'Link für den Computer kopieren',
    searchWaste: 'Abfuhrkalender suchen',
    searchWasteIn: (town: string) => `Abfuhrkalender für ${town} suchen`,
    link: 'Kalender-Link',
    linkPlaceholder: 'https://… oder webcal://…',
    checking: 'Wird geprüft …',
    check: 'Prüfen',
    found: (n: number) => (n === 1 ? '1 Termin gefunden' : `${n} Termine gefunden`),
    noneComing: 'Gerade stehen keine kommenden Termine drin – verbinden geht trotzdem.',
    looksWaste: 'Sieht nach Müllabfuhr aus – ist das euer Abfuhrkalender?',
    yesWaste: 'Ja – mit Tonnen-Erinnerung',
    noWaste: 'Nein, normaler Kalender',
    name: 'Name',
    wasteExample: 'z. B. Müllabfuhr',
    calendarExample: 'z. B. Schule, Fußballverein, Papas Kalender',
    whichBins: 'Welche Tonnen habt ihr?',
    whose: 'Wem gehören die Termine?',
    everyone: 'Allen',
    secret:
      'Der Link ist wie ein Passwort – nur Eltern sehen ihn. Die Termine kommen nur lesend herein.',
    cancel: 'Abbrechen',
    connectButton: 'Verbinden',
    feedTitle: 'Familien-Kalender aufs Handy',
    feedText:
      'Alle Familientermine erscheinen in eurem Google- oder Apple-Kalender – mit Namen, wer dabei ist. Geändert wird weiter hier.',
    getLink: 'Abo-Link holen',
    androidStep1:
      'Am Computer öffnen (Link kopieren, z. B. per Mail schicken) und dort „In Google Kalender abonnieren“ – das Handy kann das nicht selbst.',
    androidStep2: (name: string) =>
      `Danach erscheint „${name}“ in der Kalender-App. Falls nicht: Kalender-App → Einstellungen → den Kalender antippen → „Synchronisieren“ einschalten.`,
    family: 'Familie',
    copyFeed: 'Abo-Link für den Computer kopieren',
    subscribeGoogle: 'In Google Kalender abonnieren',
    subscribeApple: 'Auf iPhone / Mac abonnieren',
    copiedShort: 'Kopiert',
    copyLink: 'Link kopieren',
    oldStops: 'Der alte Link hört dann auf zu funktionieren.',
    newLink: 'Neuen Link erzeugen',
    renew: 'Neuer Link (alten sperren)',
    eveWho: 'Am Vorabend erinnern – wer stellt raus?',
    noReminder: 'Keine Erinnerung',
    firstComes: 'Wer zuerst kommt',
    starsPutOut: 'Sterne fürs Rausstellen',
    none: 'Keine',
    unknownTown:
      'Für {0} kennen wir den Abfuhrplan noch nicht direkt – mit dem Kalender-Link eures Abfallbetriebs klappt es trotzdem:',
    otherTown: 'Anderen Ort suchen',
    otherPlace: 'Anderer Ort?',
    otherCompany: 'Anderer Abfallbetrieb? Kalender-Link nutzen',
    town: 'Ort',
    townExample: 'z. B. Neutraubling',
    search: 'Suchen',
    searching: (town: string) => `Suche den Abfallbetrieb für ${town} …`,
    places: 'Passende Orte',
    whichTown: 'In welchem Ort wohnt ihr? Dann suchen wir euren Abfallbetrieb.',
    preferLink: 'Lieber den Kalender-Link nutzen',
    collectionFrom: 'Abfuhrtermine von {0} für {1} – automatisch im Familienkalender.',
    street: 'Straße',
    streetExample: 'z. B. Hauptstraße',
    streets: 'Straßen',
    change: 'Ändern',
    houseNumber: 'Hausnummer',
    numbers: 'Passende Hausnummern',
    unknownNumber: 'Diese Hausnummer kennt der Abfallbetrieb nicht.',
    findingDates: 'Suche Abfuhrtermine …',
    onlyContainers:
      'Für diese Adresse gibt es keine Haushalts-Tonnen – nur Großbehälter. Andere Hausnummer?',
    getDates: 'Abfuhrtermine holen',
  },
  en: {
    wasteLink: 'Bin calendar (link)',
    calendarLink: 'Calendar link',
    updated: (time: string) => ` · updated ${time}`,
    loading: ' · loading …',
    remindFor: (title: string) => `Reminders for ${title}`,
    refresh: (title: string) => `Update ${title} now`,
    remove: (title: string) => `Remove ${title}`,
    nothing: 'Nothing connected yet.',
    connect: 'Connect a calendar',
    waste: 'Bin collection',
    remindTitle: (title: string) => `Remind – ${title}`,
    remindHint: 'Applies to all events from this calendar – from the next update.',
    done: 'Done',
    providers: {
      google: {
        label: 'Google',
        name: 'Google Calendar',
        open: 'Open Google Calendar settings',
        steps: [
          'On the left under “Settings for my calendars”, tap the calendar.',
          'Scroll right down to “Secret address in iCal format” – Copy.',
          'Paste it below and tap “Check”.',
        ],
      },
      outlook: {
        label: 'Outlook',
        name: 'Outlook calendar',
        open: 'Open Outlook “Shared calendars”',
        steps: [
          'Under “Publish a calendar”, choose the calendar and “Can view all details”.',
          '“Publish” – then copy the ICS link.',
          'Paste it below and tap “Check”.',
        ],
      },
      apple: {
        label: 'Apple / iCloud',
        name: 'iCloud Calendar',
        open: 'Open iCloud Calendar',
        steps: [
          'Tap the share icon next to the calendar.',
          'Turn on “Public Calendar” and copy the link.',
          'Paste it below and tap “Check”.',
        ],
      },
      other: {
        label: 'School, club …',
        name: '',
        open: '',
        steps: [
          'On the website, look for “Subscribe to calendar”, “iCal” or “ICS”.',
          'Copy the link (long press → Copy link).',
          'Paste it below and tap “Check”.',
        ],
      },
      waste: {
        label: 'Bin collection',
        name: 'Bin collection',
        open: '',
        steps: [
          'Open the collection calendar of your town or waste company and choose your street.',
          'Look for “Subscribe to calendar”, “iCal” or “ICS” and copy the link (long press → Copy link).',
          'Paste it below and tap “Check”.',
        ],
      },
    },
    copied: 'Copied – e.g. email it to yourself',
    where: 'Where is the calendar?',
    computerOnly:
      'This only works in a browser on a computer – the calendar app doesn’t show this link.',
    copyForComputer: 'Copy the link for the computer',
    searchWaste: 'Search for the collection calendar',
    searchWasteIn: (town: string) => `Search for the ${town} collection calendar`,
    link: 'Calendar link',
    linkPlaceholder: 'https://… or webcal://…',
    checking: 'Checking …',
    check: 'Check',
    found: (n: number) => (n === 1 ? '1 event found' : `${n} events found`),
    noneComing: 'There are no upcoming events right now – you can still connect it.',
    looksWaste: 'Looks like bin collection – is this your collection calendar?',
    yesWaste: 'Yes – with bin reminders',
    noWaste: 'No, a normal calendar',
    name: 'Name',
    wasteExample: 'e.g. bin collection',
    calendarExample: 'e.g. school, football club, Dad’s calendar',
    whichBins: 'Which bins do you have?',
    whose: 'Whose events are these?',
    everyone: 'Everyone’s',
    secret: 'The link is like a password – only parents see it. The events come in read-only.',
    cancel: 'Cancel',
    connectButton: 'Connect',
    feedTitle: 'Family calendar on your phone',
    feedText:
      'All family events appear in your Google or Apple calendar – with the names of who’s involved. You keep making changes here.',
    getLink: 'Get subscription link',
    androidStep1:
      'Open it on a computer (copy the link, e.g. email it) and choose “Add to Google Calendar” there – the phone can’t do it itself.',
    androidStep2: (name: string) =>
      `Then “${name}” appears in the calendar app. If not: calendar app → Settings → tap the calendar → turn on “Sync”.`,
    family: 'Family',
    copyFeed: 'Copy subscription link for the computer',
    subscribeGoogle: 'Add to Google Calendar',
    subscribeApple: 'Subscribe on iPhone / Mac',
    copiedShort: 'Copied',
    copyLink: 'Copy link',
    oldStops: 'The old link will then stop working.',
    newLink: 'Create new link',
    renew: 'New link (block the old one)',
    eveWho: 'Remind the evening before – who puts the bins out?',
    noReminder: 'No reminder',
    firstComes: 'Whoever’s first',
    starsPutOut: 'Stars for putting the bins out',
    none: 'None',
    unknownTown:
      'We don’t know the collection plan for {0} directly yet – it still works with your waste company’s calendar link:',
    otherTown: 'Search for another town',
    otherPlace: 'Another town?',
    otherCompany: 'Another waste company? Use a calendar link',
    town: 'Town',
    townExample: 'e.g. Neutraubling',
    search: 'Search',
    searching: (town: string) => `Looking for the waste company for ${town} …`,
    places: 'Matching towns',
    whichTown: 'Which town do you live in? Then we’ll look for your waste company.',
    preferLink: 'Use the calendar link instead',
    collectionFrom: 'Collection days from {0} for {1} – straight into the family calendar.',
    street: 'Street',
    streetExample: 'e.g. Hauptstraße',
    streets: 'Streets',
    change: 'Change',
    houseNumber: 'House number',
    numbers: 'Matching house numbers',
    unknownNumber: 'The waste company doesn’t know this house number.',
    findingDates: 'Looking for collection days …',
    onlyContainers:
      'There are no household bins for this address – only large containers. Another house number?',
    getDates: 'Get collection days',
  },
} as const;

export const calSettingsText = CAL_SETTINGS_TEXT[locale];
